import { CARD_TIMEOUT_MINUTES, WORKFLOW_FIRST_PATH } from "@sagot/constants";
import { isConnectorCall, type Step, type StepDuration } from "./types";

/**
 * The exit slots of a step: every way a run can leave it for another path, set or not, each with a stable key.
 * The canvas (one handle per slot), the list view, the step sheet and the Add-step "Replace with" logic all read
 * and write exits through this module, so there is one source of truth. The backend mirror is `graph._targets`;
 * `contracts/workflow-exit-slots.json` is asserted by both test suites so the two cannot drift.
 */

/** Stable identity of one exit of one step. Keys marked P5-n belong to steps that phase adds. */
export type ExitKey =
  | `button:${string}` // reply_buttons.buttons[id].goto (required)
  | "start_over" // reply_buttons start over: back to Path A (fixed, no handle)
  | `branch:${number}` // branch.branches[i].goto (required)
  | "else" // branch.else_goto (optional, unset = the workflow ends)
  | "path" // go_to_path.path (required)
  | "answered_goto"
  | "not_answered_goto"
  | "asked_for_person_goto" // let_assistant_answer (unset = next step)
  | "booked_goto"
  | "not_booked_goto" // show_booking (booked unset = next step, not booked unset = ends)
  | "submitted_goto"
  | "not_submitted_goto" // show_lead_form (submitted unset = next step, not submitted unset = ends)
  | "interrupted_goto" // wait, snooze (unset = ends)
  | "woke_goto" // snooze (unset = next step)
  | "received_goto"
  | "timeout_goto" // wait_for_webhook (next step / ends)
  | "rated_goto" // ask_rating (unset = next step)
  | "replied_goto"
  | "no_reply_goto" // collect_reply (next step / ends)
  | "found_goto" // lookup_asset, M27 S3 (next step / next step; its other exit is not_found_goto below)
  | "success_goto"
  | "failure_goto" // http_request, P5-5 (next step / next step)
  | "not_found_goto"
  | "rejected_goto"
  | "unavailable_goto" // http_request as "Call connector operation", SD-SC-12 (each falls back to failure_goto)
  | "verified_goto"
  | "failed_goto" // verify_email, P5-7 (next step / ends)
  | "assigned_goto"
  | "queued_goto" // route_ticket, M27 (next step / next step)
  | "sent_goto"
  | "skipped_goto" // notify_contact, request_csat, M27 (next step / next step)
  | "duplicate_used_goto"
  | "abandoned_goto"
  | "needs_person_goto" // ticket_intake, M27 (next step / next step / ends / a person)
  | "shown_goto"
  | "not_verified_goto" // show_my_tickets, show_order_status, M27 (next step / next step)
  | "added_to_cart_goto"
  | "closed_goto" // show_catalogue, M27 S2 (next step / ends)
  | "placed_goto"
  | "sent_for_approval_goto"
  | "cancelled_goto"; // create_order, M27 S2 (next step / next step / ends; its failed_goto ends too)

/** What a run does when the exit is not connected: carry on with the next step, end, or nothing valid (a required exit still empty). */
export type ExitFallback = "next" | "end" | "unset";

export interface ExitSlot {
  key: ExitKey;
  /** i18n key of the outcome's name, used when the author wrote no label. */
  labelKey: string;
  /** Values the outcome's name uses (a card that closes: `{ count: 30 }` in "Not booked within 30 min"). */
  labelParams?: Readonly<Record<string, number>>;
  /** The author's own text (a button label, a branch label); empty when they wrote none. */
  label?: string;
  /** 1-based position among the buttons or branches of the step, for "Button 2". */
  ordinal?: number;
  required: boolean;
  fallback: ExitFallback;
  /** The path the exit leads to, or `undefined` when it is not connected. */
  target: string | undefined;
  /** A fixed exit (Start over: always Path A) is shown but never edited: no handle, no picker. */
  fixed?: boolean;
}

/** What an exit that is not connected does where it sits: carrying on with the next step ends the workflow on the last step of a path. */
export function effectiveFallback(slot: ExitSlot, lastStep: boolean): ExitFallback {
  return slot.fallback === "next" && lastStep ? "end" : slot.fallback;
}

/** One exit of one step of a definition. */
export interface ExitRef {
  path: string;
  index: number;
  key: ExitKey;
}

const X = "engage.workflows.summaries.exits";
const PATH_EXIT_LABEL = "engage.workflows.connectors.pathExit";

/** The outcome exits that are a plain optional field of the step, with what an unset one does. */
type OutcomeKey = Extract<ExitKey, `${string}_goto`>;
interface Outcome {
  key: OutcomeKey;
  /** The `summaries.exits.*` name. */
  label: string;
  fallback: "next" | "end";
}
// Keyed by step type name: the Service Desk steps (M27) are not part of the builder's `Step` union yet.
const SERVICE_DONE_OR_FAILED: readonly Outcome[] = [
  { key: "success_goto", label: "ticketStepDone", fallback: "next" },
  { key: "failure_goto", label: "ticketStepFailed", fallback: "next" },
];
const SERVICE_SENT_OR_SKIPPED: readonly Outcome[] = [
  { key: "sent_goto", label: "clientTold", fallback: "next" },
  { key: "skipped_goto", label: "clientNotTold", fallback: "next" },
];
/** "Call connector operation" (SD-SC-12): a failure leaves by the exit of its kind; one that is not connected falls back to `failure_goto`, and that one to the next step. */
const CONNECTOR_CALL_OUTCOMES: readonly Outcome[] = [
  { key: "success_goto", label: "requestSucceeded", fallback: "next" },
  { key: "not_found_goto", label: "requestNotFound", fallback: "next" },
  { key: "rejected_goto", label: "requestRejected", fallback: "next" },
  { key: "unavailable_goto", label: "requestUnavailable", fallback: "next" },
  { key: "failure_goto", label: "requestFailed", fallback: "next" },
];
const OUTCOMES: Readonly<Record<string, readonly Outcome[]>> = {
  let_assistant_answer: [
    { key: "answered_goto", label: "answered", fallback: "next" },
    { key: "not_answered_goto", label: "notAnswered", fallback: "next" },
    { key: "asked_for_person_goto", label: "askedForPerson", fallback: "next" },
  ],
  show_booking: [
    { key: "booked_goto", label: "booked", fallback: "next" },
    { key: "not_booked_goto", label: "notBooked", fallback: "end" },
  ],
  show_lead_form: [
    { key: "submitted_goto", label: "submitted", fallback: "next" },
    { key: "not_submitted_goto", label: "notSubmitted", fallback: "end" },
  ],
  wait: [{ key: "interrupted_goto", label: "interrupted", fallback: "end" }],
  snooze: [
    { key: "woke_goto", label: "woke", fallback: "next" },
    { key: "interrupted_goto", label: "interrupted", fallback: "end" },
  ],
  collect_reply: [
    { key: "replied_goto", label: "replied", fallback: "next" },
    { key: "no_reply_goto", label: "noReply", fallback: "end" },
  ],
  wait_for_webhook: [
    { key: "received_goto", label: "received", fallback: "next" },
    { key: "timeout_goto", label: "timedOut", fallback: "end" },
  ],
  ask_rating: [{ key: "rated_goto", label: "rated", fallback: "next" }],
  http_request: [
    { key: "success_goto", label: "requestSucceeded", fallback: "next" },
    { key: "failure_goto", label: "requestFailed", fallback: "next" },
  ],
  verify_email: [
    { key: "verified_goto", label: "verified", fallback: "next" },
    { key: "failed_goto", label: "verificationFailed", fallback: "end" },
  ],
  create_ticket: SERVICE_DONE_OR_FAILED,
  update_ticket: SERVICE_DONE_OR_FAILED,
  set_ticket_state: SERVICE_DONE_OR_FAILED,
  assign_ticket: SERVICE_DONE_OR_FAILED,
  route_ticket: [
    { key: "assigned_goto", label: "ticketAssigned", fallback: "next" },
    { key: "queued_goto", label: "ticketQueued", fallback: "next" },
  ],
  notify_contact: SERVICE_SENT_OR_SKIPPED,
  request_csat: SERVICE_SENT_OR_SKIPPED,
  ticket_intake: [
    { key: "submitted_goto", label: "ticketSubmitted", fallback: "next" },
    { key: "duplicate_used_goto", label: "ticketDuplicateUsed", fallback: "next" },
    { key: "abandoned_goto", label: "ticketAbandoned", fallback: "end" },
    { key: "needs_person_goto", label: "ticketNeedsPerson", fallback: "end" },
  ],
  show_ticket_form: [
    { key: "submitted_goto", label: "ticketSubmitted", fallback: "next" },
    { key: "not_submitted_goto", label: "ticketNotSubmitted", fallback: "end" },
  ],
  show_my_tickets: [
    { key: "shown_goto", label: "ticketsShown", fallback: "next" },
    { key: "not_verified_goto", label: "ticketsNotVerified", fallback: "next" },
  ],
  // After-sales (M27 S3): find an asset of the client's organisation, propose a visit.
  lookup_asset: [
    { key: "found_goto", label: "assetFound", fallback: "next" },
    { key: "not_found_goto", label: "assetNotFound", fallback: "next" },
  ],
  schedule_visit: SERVICE_DONE_OR_FAILED,
  // Orders (M27 S2): search the catalogue, read an order, and the three steps the client sees.
  lookup_catalogue: [
    { key: "found_goto", label: "productsFound", fallback: "next" },
    { key: "not_found_goto", label: "productsNotFound", fallback: "next" },
  ],
  check_order_status: SERVICE_DONE_OR_FAILED,
  show_catalogue: [
    { key: "added_to_cart_goto", label: "productAdded", fallback: "next" },
    { key: "closed_goto", label: "catalogueClosed", fallback: "end" },
  ],
  create_order: [
    { key: "placed_goto", label: "orderPlaced", fallback: "next" },
    { key: "sent_for_approval_goto", label: "orderForApproval", fallback: "next" },
    { key: "cancelled_goto", label: "orderCancelled", fallback: "end" },
    { key: "failed_goto", label: "orderFailed", fallback: "end" },
  ],
  show_order_status: [
    { key: "shown_goto", label: "ordersShown", fallback: "next" },
    { key: "not_verified_goto", label: "ordersNotVerified", fallback: "next" },
  ],
};

/** The outcome exits a step has: the connector call has its five, every other step the list of its type. */
const outcomesOf = (step: Step): readonly Outcome[] => (step.type === "http_request" && isConnectorCall(step) ? CONNECTOR_CALL_OUTCOMES : (OUTCOMES[step.type] ?? []));

/** An empty text is no text: an exit with no path chosen is `undefined`, not `""`. */
const filled = (text: string | undefined): string | undefined => (text === "" ? undefined : text);

const BUTTON_PREFIX = "button:";
const BRANCH_PREFIX = "branch:";
const buttonKey = (id: string): ExitKey => `${BUTTON_PREFIX}${id}`;
// A template literal typed `branch:${string}` cannot be narrowed to `branch:${number}`; the index is always a number here.
const branchKey = (index: number): ExitKey => `${BRANCH_PREFIX}${String(index)}` as ExitKey;

/** Every exit the step has, in the order the backend lists them; steps that never leave their path have none. */
export function exitSlotsOf(step: Step): ExitSlot[] {
  switch (step.type) {
    case "reply_buttons":
      return [
        ...step.buttons.map((button, index): ExitSlot => ({
          key: buttonKey(button.id),
          labelKey: `${X}.button`,
          label: filled(button.label.trim()),
          ordinal: index + 1,
          required: true,
          fallback: "unset",
          target: filled(button.goto),
        })),
        ...(step.start_over ? [{ key: "start_over", labelKey: `${X}.startOver`, required: false, fallback: "unset", target: WORKFLOW_FIRST_PATH, fixed: true } satisfies ExitSlot] : []),
      ];
    case "branch":
      return [
        ...step.branches.map((branch, index): ExitSlot => ({
          key: branchKey(index),
          labelKey: `${X}.branch`,
          label: filled(branch.label.trim()),
          ordinal: index + 1,
          required: true,
          fallback: "unset",
          target: filled(branch.goto),
        })),
        { key: "else", labelKey: `${X}.else`, required: false, fallback: "end", target: filled(step.else_goto) },
      ];
    case "go_to_path":
      return [{ key: "path", labelKey: PATH_EXIT_LABEL, required: true, fallback: "unset", target: filled(step.path) }];
    default:
      return outcomesOf(step).map((outcome) => ({
        key: outcome.key,
        labelKey: `${X}.${outcome.label}`,
        required: false,
        fallback: outcome.fallback,
        target: outcomeTarget(step, outcome.key),
        ...timeoutLabel(step, outcome.key),
      }));
  }
}

const DEFAULT_CARD_TIMEOUT: StepDuration = { value: CARD_TIMEOUT_MINUTES, unit: "minutes" };

/** A card that closes names its "not done" exit after the time it waits ("Not booked within 30 min"); the others keep their plain name. */
function timeoutLabel(step: Step, key: OutcomeKey): Pick<ExitSlot, "labelKey" | "labelParams"> | undefined {
  if (step.type === "show_booking" && key === "not_booked_goto") return withinLabel("notBooked", step.not_booked_after);
  if (step.type === "show_lead_form" && key === "not_submitted_goto") return withinLabel("notSubmitted", step.not_submitted_after);
  return undefined;
}

function withinLabel(name: string, after: StepDuration = DEFAULT_CARD_TIMEOUT): Pick<ExitSlot, "labelKey" | "labelParams"> {
  return { labelKey: `${X}.${name}_${after.unit}`, labelParams: { count: after.value } };
}

/** The optional `*_goto` field of a step, read by its name (only the steps listed in `OUTCOMES` have them). */
function outcomeTarget(step: Step, key: OutcomeKey): string | undefined {
  return filled((step as Partial<Record<OutcomeKey, string>>)[key]);
}

/**
 * The step with one exit pointed at `target`. Clearing a required exit leaves `""` ("not chosen yet", which the builder
 * flags); clearing an optional one removes it. An exit the step does not have changes nothing, and neither does the fixed
 * Start over exit (it always goes to Path A).
 */
export function withExit<S extends Step>(step: S, key: ExitKey, target: string | undefined): S {
  // Rewiring an exit never changes what kind of step it is.
  return rewire(step, key, target) as S;
}

function rewire(step: Step, key: ExitKey, target: string | undefined): Step {
  if (key.startsWith(BUTTON_PREFIX) && step.type === "reply_buttons") {
    const id = key.slice(BUTTON_PREFIX.length);
    return { ...step, buttons: step.buttons.map((button) => (button.id === id ? { ...button, goto: target ?? "" } : button)) };
  }
  if (key.startsWith(BRANCH_PREFIX) && step.type === "branch") {
    const at = Number(key.slice(BRANCH_PREFIX.length));
    return { ...step, branches: step.branches.map((branch, index) => (index === at ? { ...branch, goto: target ?? "" } : branch)) };
  }
  if (key === "else" && step.type === "branch") return { ...step, else_goto: filled(target) };
  if (key === "path" && step.type === "go_to_path") return { ...step, path: target ?? "" };
  if (!outcomesOf(step).some((outcome) => outcome.key === key)) return step;
  // The key was just checked against this step's own outcome fields.
  return { ...step, [key]: filled(target) };
}
